import Layout from "./components/Layout";
import { useContext } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import classes from "./App.module.scss";
import LoginForm from "./components/LoginForm";
import LoginHead from "./components/LoginHead";
import Landing from "./components/Landing";
import SendEmailForm from "./components/SendEmailForm";
import ResetForm from "./components/ResetForm";
import MfaForm from "./components/MfaForm";
import TermsForm from "./components/TermsForm";
import ValidUserContext from "./authCheck";
import { useIdleTimer } from "react-idle-timer";
import {
  FORGOT_PASSWORD_PATH,
  LOGIN_PATH,
  RESET_PATH,
  VERIFY_PATH,
  destinationFromLocation,
  isAuthPath,
} from "./routes";
import { TERMS_GATE_ENABLED } from "./constants";

function App() {
  const validUserContext = useContext(ValidUserContext);
  const location = useLocation();

  const onActive = () => {
    validUserContext.localAuthCheck(true);
  };

  useIdleTimer({ onActive, timeout: 5 * 60 * 1000 });

  const hasPasswordResetToken = Boolean(
    validUserContext.pwdResetTokenValue || validUserContext.newUserTokenValue
  );

  const showLoginStatusLabel =
    validUserContext.isLoggingIn &&
    !validUserContext.mfaRequired &&
    !validUserContext.isForgotPwd &&
    !hasPasswordResetToken;
  const loginStatusLabel =
    validUserContext.loginStatus === "sending_code"
      ? "Sending code…"
      : "Signing in…";

  const authSpinner = validUserContext.isLoggingIn && (
    <div className={classes.spinnerOverlay}>
      <div className={classes.spinnerBlock}>
        <div className={classes.spinner}></div>
        {showLoginStatusLabel && (
          <div className={classes.spinnerLabel}>{loginStatusLabel}</div>
        )}
      </div>
    </div>
  );

  const authShell = (form) => (
    <Layout>
      <div className={classes.loginCard}>
        <LoginHead />
        {form}
      </div>
      {authSpinner}
    </Layout>
  );

  if (hasPasswordResetToken && location.pathname !== RESET_PATH) {
    return <Navigate to={RESET_PATH} replace />;
  }

  if (validUserContext.isLoggedIn && !hasPasswordResetToken) {
    if (isAuthPath(location.pathname)) {
      const dest = destinationFromLocation(location.state?.from);
      return <Navigate to={dest} replace />;
    }
    if (
      TERMS_GATE_ENABLED &&
      !validUserContext.termsAccepted &&
      !validUserContext.isImpersonating
    ) {
      return (
        <Layout>
          <div className={`${classes.loginCard} ${classes.termsCard}`}>
            <LoginHead />
            <TermsForm />
          </div>
        </Layout>
      );
    }
    return (
      <div className={classes.container}>
        <Landing key={validUserContext.sessionEpoch} />
        {validUserContext.isLoggingIn && (
          <div className={classes.spinnerOverlay}>
            <div className={classes.spinner}></div>
          </div>
        )}
      </div>
    );
  }

  if (!isAuthPath(location.pathname)) {
    return <Navigate to={LOGIN_PATH} replace state={{ from: location }} />;
  }

  return (
    <Routes>
      <Route
        path={RESET_PATH}
        element={authShell(
          hasPasswordResetToken ? <ResetForm /> : <Navigate to={LOGIN_PATH} replace />
        )}
      />
      <Route path={FORGOT_PASSWORD_PATH} element={authShell(<SendEmailForm />)} />
      <Route
        path={VERIFY_PATH}
        element={authShell(
          validUserContext.mfaRequired ? (
            <MfaForm />
          ) : (
            <Navigate to={LOGIN_PATH} replace />
          )
        )}
      />
      <Route path={LOGIN_PATH} element={authShell(<LoginForm />)} />
      <Route path="*" element={<Navigate to={LOGIN_PATH} replace />} />
    </Routes>
  );
}

export default App;
